import React, { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Loader } from '@eika/loader';

import { SoeknadstypeWeb, StatusWeb, ValgtWeb } from '../../generated-types';
import { useSaveInternGjeld } from '../../hooks/useGjeld';
import { useUpdateLaantakere } from '../../hooks/useLaantaker';
import useSoeknadMedlaantaker, { useGetKjerneLaantaker } from '../../hooks/useSoeknadMedlaantaker';
import { useTranslation } from '../../translations';
import DinEpostViewEdit, { DinEpostFormData } from '../kontaktinformasjon/dinEpostViewEdit';
import DinTlfViewEdit, { DinTlfFormData } from '../kontaktinformasjon/dinTlfViewEdit';
import OppsummeringHeader from '../oppsummering/oppsummeringHeader';
import PageFooter from '../page/pageFooter';

export type MedlaantakerFormData = DinEpostFormData & DinTlfFormData;

interface Props {
    soeknadstype: SoeknadstypeWeb;
    navigateTo: string;
}

const Medlaantaker: FC<Props> = ({ soeknadstype, navigateTo }) => {
    const navigate = useNavigate();
    const { medlaantaker: transMedlaantaker } = useTranslation();
    const { data: soeknadContainer, isLoading } = useSoeknadMedlaantaker(soeknadstype);
    const { mutate: getLantaker } = useGetKjerneLaantaker();
    const { mutate: saveInternGjeld } = useSaveInternGjeld();
    const { mutate: updateLaantakere, isPending: updateLaantaker } = useUpdateLaantakere(soeknadContainer, () => {
        navigate(navigateTo);
    });
    const methods = useForm<MedlaantakerFormData>({ defaultValues: { dinEpost: undefined, dinTlf: undefined } });
    const {
        reset,
        handleSubmit,
        formState: { isDirty },
    } = methods;
    const hovedlaantaker = soeknadContainer?.laantaker.find((l) => l.erHovedlaantaker);
    const medlaantaker = soeknadContainer?.laantaker.find((l) => !l.erHovedlaantaker);

    useEffect((): void => {
        if (soeknadContainer?.soeknad) {
            saveInternGjeld(soeknadContainer.soeknad.soeknadId);
        }
    }, [soeknadContainer?.soeknad, saveInternGjeld, getLantaker]);

    useEffect((): void => {
        if (medlaantaker) {
            reset({
                dinEpost: medlaantaker?.epost?.value || undefined,
                dinTlf: medlaantaker?.tlf?.value || undefined,
            });
        }
    }, [medlaantaker, reset]);

    const onSubmitMedlaantaker = (data: MedlaantakerFormData) => {
        if (soeknadContainer?.soeknad) {
            getLantaker(soeknadContainer.soeknad.soeknadId);
        }

        if (isDirty && hovedlaantaker && medlaantaker && soeknadContainer?.soeknad) {
            medlaantaker.tlf = { value: data.dinTlf || '' };
            medlaantaker.epost = { value: data.dinEpost || '' };
            medlaantaker.status = StatusWeb.CHANGE;
            const laantakere = [hovedlaantaker, medlaantaker];
            updateLaantakere({
                soeknadId: soeknadContainer?.soeknad.soeknadId,
                annenBolig: soeknadContainer?.soeknad.annenBolig,
                visValgHarMedlaantaker: true,
                harMedlaantaker: ValgtWeb.IKKE_VALGT,
                laantakere,
            });
        } else {
            navigate(navigateTo);
        }
    };

    const ingressText = (soeknadstype: SoeknadstypeWeb) => {
        switch (soeknadstype) {
            case SoeknadstypeWeb.FINANSIERINGSBEVIS:
                return transMedlaantaker.infoFinasiertingsbevis;
            case SoeknadstypeWeb.FLYTTE_BOLIGLAAN:
                return transMedlaantaker.infoFlytteBoliglaan;
            case SoeknadstypeWeb.KJOEPE_BOLIG:
                return transMedlaantaker.infoKjoepeBolig;
            case SoeknadstypeWeb.REFINANSIERE_BOLIGLAAN:
                return transMedlaantaker.infoOekeBoliglaan;
            default:
                return transMedlaantaker.infoKjoepeBolig;
        }
    };

    if (isLoading) return <Loader className="padding-top-extra-large" />;

    return (
        <div>
            <OppsummeringHeader soeknadContainer={soeknadContainer} />
            <div className="sgw-layout medium-centered-container">
                <div className="sgw-layout-item">
                    <FormProvider {...methods}>
                        <form onSubmit={handleSubmit(onSubmitMedlaantaker)} className="row row-gap-large">
                            <h2 className="margin-desktop-top-larger">
                                {transMedlaantaker.titleOppfoertSomMedlaantaker}
                            </h2>
                            {soeknadContainer?.soeknad?.soeknadtype && (
                                <span className="typography-ingress__light">
                                    {ingressText(soeknadContainer?.soeknad?.soeknadtype).replace(
                                        '{navn}',
                                        hovedlaantaker ? `${hovedlaantaker.fornavn} ${hovedlaantaker.etternavn}` : '',
                                    )}
                                </span>
                            )}
                            <h3>{transMedlaantaker.subTitleKontaktinfo}</h3>
                            <DinEpostViewEdit />
                            <DinTlfViewEdit />
                            <PageFooter
                                soeknadId={soeknadContainer?.soeknad?.soeknadId}
                                showSpinner={updateLaantaker}
                                startPage
                            />
                        </form>
                    </FormProvider>
                </div>
            </div>
        </div>
    );
};

export default Medlaantaker;
